import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './SignIn.css';

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const EyeOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
    <line x1="1" y1="1" x2="23" y2="23"></line>
  </svg>
);

const SignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signin, signout, sendVerificationEmail } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);
      const userCredential = await signin(email, password);
      if (!userCredential.user.emailVerified) {
        setError('Please verify your email before signing in. A new verification email has been sent to your inbox.');
        await sendVerificationEmail();
        await signout();
        setLoading(false);
        return;
      }
      navigate('/');
    } catch (err) {
      switch (err.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          setError('Invalid email or password.');
          break;
        case 'auth/invalid-email':
          setError('The email address is not valid.');
          break;
        default:
          setError('Failed to sign in. Please try again.');
          break;
      }
    }
    setLoading(false);
  };

  return (
    <>
      <title>Sign In - WizeHelper</title>
      <div className="signin-container" data-testid="signIn-container">
        <div className="signin-card" data-testid="signIn-card">
          <h1 className="signin-title" data-testid="signIn-title">Sign In</h1>
          <form onSubmit={handleSubmit} className="signin-form" data-testid="signIn-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
                autoComplete="email"
                data-testid="signIn-emailInput"
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  data-testid="signIn-passwordInput"
                />
                <button
                  type="button"
                  className="password-toggle-button"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="signIn-passwordVisibilityButton"
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              <div className="forgot-password-link">
                <Link to="/forgot-password" data-testid="signIn-forgotPasswordLink">Forgot Password?</Link>
              </div>
            </div>
            {error && <p className="error-message" data-testid="signIn-errorMessage">{error}</p>}
            <button type="submit" className="submit-button" disabled={loading} data-testid="signIn-submitButton">
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>
          <div className="switch-form-link">
            <p>Don't have an account? <Link to="/signup" data-testid="signIn-signUpLink">Sign Up</Link></p>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignIn;